/**
 * Builds the Google consent URL in the browser.
 *
 * Same approach as QuickBooks / Stripe / Chargebee in this codebase: the
 * authorization request carries nothing secret — client_id is public by design —
 * so there is no reason to spend a round trip asking the backend to assemble it.
 * Only the code->token exchange needs the client secret, and that stays server-side.
 */

export const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";

/** Read-only Analytics. A Google "sensitive" scope — the consent screen needs verifying. */
export const ANALYTICS_READONLY_SCOPE = "https://www.googleapis.com/auth/analytics.readonly";

/**
 * Read at module load. Webpack's DefinePlugin only substitutes this token when the
 * active build config actually defines GOOGLE_OAUTH_CLIENT_ID; when it doesn't (e.g.
 * the local `npm start` config), the raw `process.env.…` reference survives into the
 * bundle and throws `process is not defined` in the browser — crashing the whole
 * route. Guarding the read keeps the module import-safe either way.
 */
const CLIENT_ID: string | undefined = (() => {
    try {
        return process.env.GOOGLE_OAUTH_CLIENT_ID as string | undefined;
    } catch {
        return undefined;
    }
})();
const NONCE_KEY = "citedby.revenueAttribution.oauthNonce";

export function hasGoogleClientId(): boolean {
    return Boolean(CLIENT_ID);
}

/**
 * Google matches redirect_uri exactly against what's registered in the Cloud
 * console, so this must stay free of query strings — origin + pathname only.
 */
export function ga4RedirectUri(): string {
    if (typeof window === "undefined") return "";
    return `${window.location.origin}${window.location.pathname}`;
}

function makeNonce(): string {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Builds the consent URL and remembers a nonce so the value Google echoes back in
 * `state` can be checked on return — without it, any third party could hand the
 * user a crafted callback URL.
 */
export function buildGoogleConsentUrl(): string {
    const nonce = makeNonce();
    try {
        sessionStorage.setItem(NONCE_KEY, nonce);
    } catch {
        /* storage blocked — the exchange still works, we just can't verify the nonce */
    }

    const params = new URLSearchParams({
        response_type: "code",
        client_id: CLIENT_ID ?? "",
        redirect_uri: ga4RedirectUri(),
        scope: ANALYTICS_READONLY_SCOPE,
        // Both are required to get a refresh token back. Without them the sync
        // dies an hour after setup, and it fails silently.
        access_type: "offline",
        prompt: "consent",
        include_granted_scopes: "true",
        state: JSON.stringify({ source: "ga4_revenue_attribution", nonce })
    });

    return `${GOOGLE_AUTH_URL}?${params.toString()}`;
}

/** True when the returned state carries the nonce we issued. */
export function verifyOAuthState(rawState: string | null): boolean {
    let expected: string | null = null;
    try {
        expected = sessionStorage.getItem(NONCE_KEY);
        sessionStorage.removeItem(NONCE_KEY);
    } catch {
        return true; // Couldn't store it in the first place — don't block the user.
    }
    if (!expected) return true;
    if (!rawState) return false;
    try {
        return (JSON.parse(rawState) as { nonce?: string }).nonce === expected;
    } catch {
        return false;
    }
}
